/**
 * WhatsApp Chat V2 - Tipi TypeScript
 *
 * Definizioni per la nuova architettura realtime-first.
 * Riferimento: MD/WHATSAPP_CHAT_V2_PLAN.md
 */

// ============ ENUMS ============

export type MessageType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'ptt'        // Push-to-talk (voice note)
  | 'document'
  | 'sticker'
  | 'location'
  | 'contact'
  | 'poll'
  | 'voice'       // Vocale salvato con questo nome da alcuni motori WAHA
  | 'ciphertext'  // Messaggio WAHA non ancora decifrato
  | 'call_log'    // Riga di sistema: chiamata (scritta dal core)
  | 'system';     // Altre righe di sistema

export type MessageStatus =
  | 'pending'    // In attesa di invio
  | 'sent'       // Inviato al server (✓)
  | 'delivered'  // Consegnato (✓✓)
  | 'read'       // Letto (✓✓ blu)
  | 'played'     // Audio riprodotto
  | 'failed';    // Errore invio

export type PresenceStatus = 'online' | 'offline' | 'typing' | 'recording' | 'paused';

export type MessageDirection = 'inbound' | 'outbound';

// ============ INTERFACES ============

/**
 * Chat overview (dalla API WAHA /chats/overview)
 */
export interface ChatOverview {
  id: string;                // JID: 39123456789@c.us o group@g.us
  name: string;              // Nome contatto o gruppo
  isGroup: boolean;
  profilePicUrl?: string;
  lastMessage?: {
    body: string;
    timestamp: number;
    fromMe: boolean;
    type: MessageType;
    senderJid?: string;      // JID del sender (per gruppi)
    senderName?: string;     // Nome del sender (per gruppi)
    /** Stato dell'ultimo messaggio inviato (ack WAHA: 1 inviato, 2 consegnato, 3/4 letto). Assente se la fonte non lo da'. */
    ack?: number;
  };
  unreadCount?: number; // undefined se WAHA non fornisce il dato
  /** Solo numeri WAHA: istante (ms) fino a cui WAHA riporta la chat come letta
   * (0 non letti, es. letta dal telefono). undefined se WAHA non lo dice. */
  wahaReadUpToMs?: number;
  pinned: boolean;
  archived: boolean;
  participantsCount?: number; // Solo per gruppi
  /** Conversazione creata sul posto dal CRM (numero mai contattato): vive solo in memoria finche' non parte un messaggio. */
  isDraft?: boolean;
}

/**
 * Reaction emoji su un messaggio
 */
export interface MessageReaction {
  emoji: string;
  count: number;
  senderJids: string[];  // Chi ha reagito con questa emoji
}

/**
 * Messaggio dal DB Supabase (whatsapp_messages)
 */
export interface Message {
  id: string;                // ID WAHA (es: "true_39123456789@c.us_ABCD1234")
  chatId: string;            // JID del contatto/gruppo
  content: string;           // Testo messaggio
  type: MessageType;
  direction: MessageDirection;
  status: MessageStatus;
  timestamp: Date;
  // Media
  mediaUrl?: string;
  mediaMimetype?: string;
  mediaFilename?: string;
  // Reply
  replyToId?: string;
  replyToBody?: string;
  // Gruppo
  senderJid?: string;        // Chi ha inviato (per gruppi)
  senderName?: string;
  // Metadata
  isEdited?: boolean;
  isDeleted?: boolean;
  isForwarded?: boolean;
  // Reactions
  reactions?: MessageReaction[];
  // Trascrizione audio (da Whisper)
  transcription?: string;
  // Menzioni (JID delle persone taggate)
  mentionedJidList?: string[];
  // Sender tracking (per sessioni condivise)
  senderUserId?: string;
  senderUserName?: string;
  /** whatsapp_messages.metadata (numeri Ufficiale: error, location, contacts, kind). */
  metadata?: Record<string, unknown>;
  /** Motivo dell'invio fallito in italiano (da metadata.error), solo se c'e'. */
  failureReason?: string;
}

/**
 * Messaggio in attesa di conferma (UI ottimistica)
 */
export interface PendingMessage {
  tempId: string;            // ID temporaneo client-side (temp-uuid)
  chatId: string;
  content: string;
  type: MessageType;
  mediaFile?: File;
  mediaPreviewUrl?: string;  // URL.createObjectURL per preview
  replyToId?: string;
  replyToBody?: string;
  timestamp: Date;
  status: 'sending' | 'failed';
  retryCount: number;
  error?: string;
}

/**
 * Stato presenza contatto
 */
export interface ContactPresence {
  chatId: string;
  status: PresenceStatus;
  lastSeen?: Date;
}

/**
 * Configurazione WAHA
 */
export interface WahaConfig {
  apiUrl: string;
  apiKey: string;
  session: string;
  phoneNumber: string;
  organizationId: string;
  configId: string;
  /**
   * Service name dal record `api_configurations` ('waha' | 'whatsapp_cloud' | 'whatsapp_cloud_coex').
   * Opzionale per backwards-compat: il nome legacy `WahaConfig` resta ma il campo
   * permette al chat header di mostrare il badge provider corretto.
   */
  serviceName?: 'waha' | 'whatsapp_cloud' | 'whatsapp_cloud_coex' | string;
}

/**
 * Risultato invio messaggio WAHA
 */
export interface WahaSendResult {
  id: string;                // ID messaggio WAHA
  timestamp: number;
  status: 'sent' | 'queued';
}

/**
 * Opzioni per caricare messaggi
 */
export interface LoadMessagesOptions {
  limit?: number;
  offset?: number;
  beforeTimestamp?: number;
  downloadMedia?: boolean;
}

/**
 * Risposta API WAHA per lista chat
 * Nota: WAHA GOWS può restituire id come stringa o come oggetto
 * Il campo unreadCount può essere a livello root o dentro _chat (dipende dal motore)
 */
export interface WahaChatResponse {
  id: string | { _serialized: string; user: string; server: string };
  name: string;
  isGroup: boolean;
  unreadCount?: number;
  timestamp: number;
  lastMessage?: {
    body: string;
    timestamp: number;
    fromMe: boolean;
    type: string;
    ack?: number;            // Stato consegna (WAHA /chats/overview)
    from?: string;           // JID del sender
    notifyName?: string;     // Nome del sender
    pushName?: string;       // Nome alternativo
    _data?: {
      notifyName?: string;
      pushName?: string;
    };
  };
  groupMetadata?: {
    subject: string;
    participants: Array<{ id: string; isAdmin: boolean }>;
  };
  // Campo engine-specific che può contenere dati aggiuntivi come unreadCount
  _chat?: {
    unreadCount?: number;
    [key: string]: unknown;
  };
}

/**
 * Risposta API WAHA per messaggio
 */
export interface WahaMessageResponse {
  id: string | { _serialized: string; id?: string };
  body: string;
  type: string;
  timestamp: number;
  from: string;
  to: string;
  fromMe: boolean;
  hasMedia: boolean;
  mediaUrl?: string;
  mimetype?: string;
  filename?: string;
  ack: number;
  // Vecchio formato della citazione (WEBJS / versioni WAHA precedenti)
  quotedMsg?: {
    id: string | { _serialized: string };
    body: string;
  };
  // Citazione nello schema WAMessage di WAHA 2026.10 (ReplyToMessage): id corto del messaggio citato
  replyTo?: {
    id: string;
    participant?: string;
    body?: string;
    hasMedia?: boolean;
  };
  // Sender name fields (per gruppi)
  notifyName?: string;
  pushName?: string;
  // WAHA nested structures (come nel webhook)
  _data?: {
    body?: string;
    type?: string;
    mimetype?: string;
    notifyName?: string;
    pushName?: string;
    [key: string]: unknown;
  };
  media?: {
    url?: string;
    mimetype?: string;
    filename?: string;
  };
}

// ============ ACK MAPPING ============

/**
 * Mapping ACK WAHA → MessageStatus
 */
export const ACK_STATUS_MAP: Record<number, MessageStatus> = {
  0: 'pending',    // ⏱️ Clock
  1: 'sent',       // ✓ Singola spunta grigia
  2: 'delivered',  // ✓✓ Doppia spunta grigia
  3: 'read',       // ✓✓ Doppia spunta blu
  4: 'played',     // ✓✓ Audio riprodotto
};

// ============ HELPERS ============

/**
 * Estrae chatId dal JID (rimuove @c.us o @g.us)
 */
export function extractPhoneFromJid(jid: string): string {
  return jid.split('@')[0];
}

/**
 * Costruisce JID da numero di telefono
 */
export function buildJid(phone: string, isGroup = false): string {
  const cleanPhone = phone.replace(/\D/g, '');
  return `${cleanPhone}@${isGroup ? 'g.us' : 'c.us'}`;
}

/**
 * Verifica se un JID è un gruppo
 */
export function isGroupJid(jid: string): boolean {
  return jid.endsWith('@g.us');
}
